import { useCallback, useEffect, useState } from "react";
import { ListChecks, UserCircle } from "@phosphor-icons/react";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { api } from "../api";
import { WordEditor } from "./WordEditor";
import { STATUS_VARIANT } from "./AdminWords";

export default function AdminReview() {
  const [items, setItems] = useState(null);
  const [editor, setEditor] = useState(null);

  const load = useCallback(async () => {
    const [drafts, reviews] = await Promise.all([
      api("/admin/words?status=draft&limit=100"),
      api("/admin/words?status=review&limit=100"),
    ]);
    setItems([...reviews.items, ...drafts.items]);
  }, []);

  useEffect(() => { load().catch(() => setItems([])); }, [load]);

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-review">
      <h1 className="font-display text-3xl font-black tracking-tight text-foreground">Review Queue</h1>
      <p className="mt-1 text-sm font-semibold text-muted-foreground">
        Draft and AI-enriched words wait here. Nothing reaches children until you approve it.
      </p>

      <div className="mt-6 space-y-2">
        {items === null && <div className="h-40 animate-pulse rounded-3xl bg-muted" />}
        {items?.map((w) => (
          <button key={w.word_id} data-testid={`review-row-${w.normalized}`} onClick={() => setEditor(w)}
            className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3.5 text-left shadow-soft transition hover:border-primary/40 sm:px-5">
            <span className="min-w-0 flex-1">
              <span className="block font-display text-base font-extrabold capitalize text-foreground">{w.word}</span>
              <span className="block truncate text-xs font-semibold text-muted-foreground">{w.meaning || "Needs content — add a meaning, synonyms and sentences"}</span>
              {w.source === "parent_request" && w.requested_by_name && (
                <span className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-muted-foreground/70">
                  <UserCircle size={11} weight="bold" />
                  {w.requested_by_name}{w.requested_by_email ? ` · ${w.requested_by_email}` : ""}
                </span>
              )}
            </span>
            {!w.meaning && <BloomBadge variant="warning" className="hidden px-2 py-0.5 text-[10px] sm:inline-flex">Needs content</BloomBadge>}
            <BloomBadge variant={STATUS_VARIANT[w.status]} className="px-2.5 py-0.5 text-xs capitalize">{w.status}</BloomBadge>
          </button>
        ))}
        {items?.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-3xl border border-dashed border-border p-10 text-center">
            <ListChecks size={32} weight="bold" className="text-muted-foreground" />
            <p className="text-sm font-semibold text-muted-foreground">The queue is clear — nothing waiting for review.</p>
          </div>
        )}
      </div>

      {editor && <WordEditor word={editor} onClose={() => setEditor(null)} onSaved={load} />}
    </div>
  );
}
